import { FormHelperText, Tab, Tabs, TextareaAutosize } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import { zodResolver } from "@hookform/resolvers/zod";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { JobPhaseAutocomplete } from "components/JobPhaseScrollAsyncAutocomplete/JobPhaseAutocomplete";
import ScrollAsyncAutocomplete from "components/ScrollAsyncAutocomplete/ScrollAsyncAutocomplete";
import CustomSelect from "components/Shared/CustomSelect/CustomSelect";
import AutoComplete from "modules/AutoComplete/AutoComplete";
import OrganizationUsersOptions from "modules/organization/OrganizationUsersOptions/OrganizationUsersOptions";
import dayjs from "dayjs";
import useGetJobPhaseLazy from "hooks/jobs/job-phases/useGetJobPhaseLazy";
import useGetJobLazy from "hooks/jobs/useGetJobLazy";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import { useGetTimeBuckets } from "hooks/timeTracking/useGetTimeBuckets";
import { useDeleteTimeEntry } from "hooks/timeTracking/useDeleteTimeEntry";
import { JobPhase, UserStatus } from "generated/graphql";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type AssociationType = "jobPhase" | "timeBucket";

interface EditTimeEntryFormProps {
  onSubmit: (data: any) => Promise<void>;
  onClose: () => void;
  defaultValues: any;
  schema: any;
  entry: any;
}

const dateInputStyles = {
  height: "32px",
  width: "100%",
  fontSize: "0.875rem",
  borderRadius: "8px",
  border: "1px solid #d2d6da",
  color: "#344767",
  padding: "25px 13px",
};

export default function EditTimeEntryForm({
  onSubmit,
  onClose,
  defaultValues,
  schema,
  entry,
}: EditTimeEntryFormProps) {
  const [associationType, setAssociationType] = useState<AssociationType>(
    defaultValues.timeBucketId ? "timeBucket" : "jobPhase"
  );

  const [getJobPhase, { data: jobPhaseData }] = useGetJobPhaseLazy(false);
  const [getJob, { job }] = useGetJobLazy();
  const [deleteTimeEntry] = useDeleteTimeEntry();
  const { data: timeBuckets, loading: timeBucketsLoading } = useGetTimeBuckets();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const jobPhaseId = watch("jobPhaseId");
  const modalEntryId = entry?.id || "new";
  const lastFetchedJobPhaseIdRef = useRef<string | null>(null);
  const lastFetchedJobIdRef = useRef<string | null>(null);
  const contextJobPhaseId = jobPhaseId || entry?.jobPhase?.id || null;
  const contextJobId = entry?.jobPhase?.job?.id || null;

  const acceptFn = async () => {
    if (!entry?.id) {
      return false;
    }

    const result = await deleteTimeEntry({
      variables: { id: entry.id },
    });

    if (result.success) {
      onClose();
    }

    return result.success;
  };

  const cancelFn = async () => true;

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  const customerText = useMemo(() => {
    const customerName = job?.company?.name || entry?.jobPhase?.job?.company?.name;
    return customerName || "N/A";
  }, [entry, job]);

  const selectedJobPhaseForAutocomplete = useMemo(() => {
    const phase = jobPhaseData?.getJobPhase;

    if (!phase) {
      return null;
    }

    const companyName = job?.company?.name || entry?.jobPhase?.job?.company?.name || null;
    const phaseAsAny = phase as any;

    return {
      ...phaseAsAny,
      job: {
        ...(phaseAsAny.job || {}),
        company: {
          ...(phaseAsAny.job?.company || {}),
          name: companyName,
        },
      },
    } as JobPhase;
  }, [entry, job, jobPhaseData]);

  const jobText = useMemo(() => {
    const phaseName =
      jobPhaseData?.getJobPhase?.proposalStage?.name || entry?.jobPhase?.proposalStage?.name;
    const jobExternalId =
      job?.externalId || entry?.jobPhase?.job?.externalId || entry?.jobPhase?.externalId;
    const normalizedPhaseName = phaseName && phaseName !== "undefined" ? phaseName : null;
    const normalizedJobExternalId =
      jobExternalId && jobExternalId !== "undefined" ? jobExternalId : null;

    if (normalizedPhaseName && normalizedJobExternalId) {
      return `${normalizedPhaseName} (${normalizedJobExternalId})`;
    }

    if (normalizedPhaseName) {
      return normalizedPhaseName;
    }

    if (normalizedJobExternalId) {
      return normalizedJobExternalId;
    }

    return "N/A";
  }, [entry, job, jobPhaseData]);

  useEffect(() => {
    if (contextJobPhaseId && lastFetchedJobPhaseIdRef.current !== contextJobPhaseId) {
      lastFetchedJobPhaseIdRef.current = contextJobPhaseId;
      getJobPhase(contextJobPhaseId);
    }
  }, [contextJobPhaseId]);

  useEffect(() => {
    if (contextJobId && lastFetchedJobIdRef.current !== contextJobId) {
      lastFetchedJobIdRef.current = contextJobId;
      getJob(contextJobId);
    }
  }, [contextJobId]);

  useEffect(() => {
    if (associationType === "jobPhase") {
      setValue("timeBucketId", null);
    } else {
      setValue("jobPhaseId", null);
    }
  }, [associationType, setValue]);

  useEffect(() => {
    const initMap = () => {
      if (!window.google?.maps) {
        return;
      }

      const clockInMapElement = document.getElementById(
        `edit-time-entry-clock-in-map-${modalEntryId}`
      );
      const clockOutMapElement = document.getElementById(
        `edit-time-entry-clock-out-map-${modalEntryId}`
      );

      if (clockInMapElement && entry?.clockInLatitude && entry?.clockInLongitude) {
        const location = new google.maps.LatLng(entry.clockInLatitude, entry.clockInLongitude);
        const map = new google.maps.Map(clockInMapElement, {
          center: location,
          zoom: 15,
        });

        new google.maps.Marker({
          position: location,
          map,
        });
      }

      if (clockOutMapElement && entry?.clockOutLatitude && entry?.clockOutLongitude) {
        const location = new google.maps.LatLng(entry.clockOutLatitude, entry.clockOutLongitude);
        const map = new google.maps.Map(clockOutMapElement, {
          center: location,
          zoom: 15,
        });

        new google.maps.Marker({
          position: location,
          map,
        });
      }
    };

    if (!window.google?.maps) {
      const checkGoogleMaps = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(checkGoogleMaps);
          initMap();
        }
      }, 100);

      return () => clearInterval(checkGoogleMaps);
    }

    const timeout = setTimeout(() => {
      initMap();
    }, 100);

    return () => clearTimeout(timeout);
  }, [
    entry,
    entry?.clockInLatitude,
    entry?.clockInLongitude,
    entry?.clockOutLatitude,
    entry?.clockOutLongitude,
    modalEntryId,
  ]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Delete Time Entry"
        content="Are you sure you want to delete this time entry?"
      />

      <MDBox
        sx={{
          border: "1px solid #d1d5db",
          borderRadius: "10px",
          p: 1.5,
          backgroundColor: "#f8fafc",
          mb: 2,
        }}
      >
        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto auto" },
              alignItems: "center",
              columnGap: 3,
              rowGap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="button" fontWeight="bold" color="text" display="block">
                Customer
              </MDTypography>
              <MDTypography
                variant="button"
                color="secondary"
                display="block"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {customerText}
              </MDTypography>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography variant="button" fontWeight="bold" color="text" display="block">
                Job
              </MDTypography>
              <MDTypography
                variant="button"
                color="secondary"
                display="block"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {jobText}
              </MDTypography>
            </MDBox>
          </MDBox>

          <Tabs
            value={associationType}
            onChange={(_, newValue) => setAssociationType(newValue)}
            sx={{
              minHeight: "38px",
              "& .MuiTab-root": { minHeight: "38px" },
              alignSelf: { xs: "stretch", sm: "flex-start" },
            }}
          >
            <Tab label="Job Phase" value="jobPhase" />
            <Tab label="Time Bucket" value="timeBucket" />
          </Tabs>
        </MDBox>
      </MDBox>

      <MDBox
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.2fr 1fr" },
          gap: 2,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        <MDBox>
          <MDBox mb={2}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              Select User
            </MDTypography>
            <OrganizationUsersOptions
              queryOverride={{ first: 1000, status: UserStatus.ACTIVE }}
              archivedUser={entry?.user}
            >
              {({ organizationUsersOptions, loading, isOptionDisabled }) => (
                <Controller
                  control={control}
                  name="userId"
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <AutoComplete
                        options={organizationUsersOptions}
                        loading={loading}
                        variant="outlined"
                        field={{
                          onChange: field.onChange,
                          onBlur: field.onBlur,
                          value: field.value,
                          name: field.name,
                          ref: field.ref,
                        }}
                        error={error}
                        placeholder="Select User"
                        getOptionDisabled={isOptionDisabled}
                      />
                      <FormHelperText error>{(errors as any)?.userId?.message}</FormHelperText>
                    </>
                  )}
                />
              )}
            </OrganizationUsersOptions>
          </MDBox>

          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            {associationType === "jobPhase" ? "Select Job Phase" : "Select Time Bucket"}
          </MDTypography>

          {associationType === "jobPhase" && (
            <MDBox mb={2}>
              <JobPhaseAutocomplete selectedJobPhase={selectedJobPhaseForAutocomplete}>
                {({ options, queryLoading, pagination, handleSearch }) => (
                  <Controller
                    control={control}
                    name="jobPhaseId"
                    render={({ field: { onChange, value } }) => (
                      <ScrollAsyncAutocomplete
                        label=""
                        value={options.find((x) => x.value === value)}
                        onChange={onChange}
                        options={options}
                        loading={queryLoading}
                        hasMore={pagination.hasNextPage}
                        search={handleSearch}
                        onLoadMore={pagination.loadMore}
                      />
                    )}
                  />
                )}
              </JobPhaseAutocomplete>

              {jobPhaseId && (
                <MDBox mt={1}>
                  <MDTypography
                    variant="button"
                    color="info"
                    sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5 }}
                    onClick={() => {
                      setValue("jobPhaseId", null);
                    }}
                  >
                    <ClearIcon fontSize="small" sx={{ display: "flex" }} />
                    Clear Selection
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          )}

          {associationType === "timeBucket" && (
            <MDBox mb={2}>
              <Controller
                control={control}
                name="timeBucketId"
                render={({ field }) => (
                  <>
                    <CustomSelect
                      label=""
                      control={control}
                      disabled={timeBucketsLoading}
                      selectVariant="outlined"
                      fullWidth
                      data={
                        timeBuckets?.map((bucket) => ({
                          value: bucket.id,
                          label: bucket.name,
                        })) || []
                      }
                      {...field}
                    />

                    {field.value && (
                      <MDBox mt={1}>
                        <MDTypography
                          variant="button"
                          color="info"
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                          onClick={() => field.onChange(null)}
                        >
                          <ClearIcon fontSize="small" sx={{ display: "flex" }} />
                          Clear Selection
                        </MDTypography>
                      </MDBox>
                    )}
                  </>
                )}
              />
            </MDBox>
          )}

          <MDBox
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <MDBox display="flex" flexDirection="column">
              <label style={{ fontSize: "0.875rem", color: "#7b809a", marginBottom: "6px" }}>
                Clock In
              </label>
              <Controller
                control={control}
                name="startTime"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    selected={value}
                    onChange={onChange}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd hh:mm a"
                    className="date-input"
                    customInput={<input style={dateInputStyles} />}
                  />
                )}
              />
              <FormHelperText error>{errors.startTime?.message as string}</FormHelperText>
            </MDBox>

            <MDBox display="flex" flexDirection="column">
              <label style={{ fontSize: "0.875rem", color: "#7b809a", marginBottom: "6px" }}>
                Clock Out
              </label>
              <Controller
                control={control}
                name="endTime"
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    selected={value}
                    onChange={onChange}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd hh:mm a"
                    className="date-input"
                    customInput={<input style={dateInputStyles} />}
                  />
                )}
              />
              <FormHelperText error>{errors.endTime?.message as string}</FormHelperText>
            </MDBox>
          </MDBox>

          <MDBox mb={2}>
            <MDTypography
              variant="button"
              fontWeight="regular"
              color="secondary"
              mb={0.5}
              display="block"
            >
              Notes
            </MDTypography>
            <TextareaAutosize
              minRows={5}
              style={{
                width: "100%",
                borderRadius: "10px",
                border: "1px solid #d2d6da",
                padding: "10px",
                fontFamily: "inherit",
                fontSize: "0.875rem",
              }}
              {...register("notes")}
            />
          </MDBox>
        </MDBox>

        <MDBox>
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Location Verification
          </MDTypography>

          <MDBox
            mb={2}
            sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}
          >
            <MDBox
              px={1.5}
              py={1}
              sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f8fafc" }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MDTypography variant="button">Clock In Location</MDTypography>
              <MDTypography variant="caption" color="secondary">
                {entry?.startTime ? dayjs(entry.startTime).format("hh:mm A") : "N/A"}
              </MDTypography>
            </MDBox>
            {entry?.clockInLatitude && entry?.clockInLongitude ? (
              <div
                id={`edit-time-entry-clock-in-map-${modalEntryId}`}
                style={{ width: "100%", height: "220px" }}
              />
            ) : (
              <MDBox p={2}>
                <MDTypography variant="body2">No clock in location available</MDTypography>
              </MDBox>
            )}
          </MDBox>

          <MDBox sx={{ border: "1px solid #e5e7eb", borderRadius: "10px", overflow: "hidden" }}>
            <MDBox
              px={1.5}
              py={1}
              sx={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f8fafc" }}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <MDTypography variant="button">Clock Out Location</MDTypography>
              <MDTypography variant="caption" color="secondary">
                {entry?.endTime ? dayjs(entry.endTime).format("hh:mm A") : "N/A"}
              </MDTypography>
            </MDBox>
            {entry?.clockOutLatitude && entry?.clockOutLongitude ? (
              <div
                id={`edit-time-entry-clock-out-map-${modalEntryId}`}
                style={{ width: "100%", height: "220px" }}
              />
            ) : (
              <MDBox p={2}>
                <MDTypography variant="body2">No clock out location available</MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>

      <MDBox
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: 3,
          backgroundColor: "white",
          borderTop: "1px solid #e5e7eb",
          pt: 2,
          pb: 1.5,
          mt: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <MDButton
          type="button"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setOpen(true)}
        >
          Delete
        </MDButton>

        <MDBox display="flex" gap={1}>
          <MDButton type="button" variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </MDButton>
          <MDButton type="submit" variant="gradient" color="success">
            Save
          </MDButton>
        </MDBox>
      </MDBox>
    </form>
  );
}
